<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import { importLibrary } from '@/utils/google-maps-loader';
import { useCompanyMapCenter, COUNTRY_ZOOM } from '@/composables/useCompanyMapCenter';
import type { ShiftTodayListItem } from '@/api/shifts';
import type { SerializedUser } from '@/api/users';
import {
  bubbleRadius,
  groupShiftsByLocation,
  metersPerPixel,
  pulseFrame,
  veilPath,
  RESTING_FRAME,
  type PulseFrame,
  type ShiftLocation,
  type VeilRing,
} from './shifts-today-map.utils';

const props = defineProps<{
  shifts: ShiftTodayListItem[];
  loading: boolean;
}>();

// Street level is enough to tell patients apart; closer only adds noise.
const MAX_FIT_ZOOM = 15;
const FIT_BOUNDS_PADDING = 48;
// Look of the ActiveAdmin live map (admin/active-shifts-map), on its dark map.
const BUBBLE_COLOR = '#b3beff';
const BUBBLE_STROKE_OPACITY = 1;
const BUBBLE_STROKE_WEIGHT = 2;
const PULSE_FRAME_MS = 50;
// The veil over neighbouring countries' land: it dims their shapes, roads and
// small names. Google draws its main city labels above any shape, so those still show.
const VEIL_COLOR = '#000';
const VEIL_OPACITY = 0.7;
const Z_INDEX = { veil: 0, bubble: 1 };

// Land of the countries around each company country, one lazy chunk per country
// (built by bin/build-country-veils).
const countryVeils = import.meta.glob<VeilRing[]>('./country-veils/*.json', { import: 'default' });

const companyMapCenter = useCompanyMapCenter();
const currentUser = inject<Ref<SerializedUser | undefined>>('currentUser');

const mapContainer = ref<HTMLElement | null>(null);
const mapError = ref(false);
// Whole zoom steps only, so a smooth zoom does not regroup on every frame.
const groupingZoom = ref(COUNTRY_ZOOM);

type Bubble = { circle: google.maps.Circle; radiusPx: number; lat: number };

let map: google.maps.Map | null = null;
let infoWindow: google.maps.InfoWindow | null = null;
let bubbles: Bubble[] = [];
let veil: google.maps.Polygon[] = [];
let pulseTimer: ReturnType<typeof setInterval> | null = null;
let hasFitBounds = false;

const locations = computed(() => groupShiftsByLocation(props.shifts, groupingZoom.value));
const mappedCount = computed(() => locations.value.reduce((sum, loc) => sum + loc.shifts.length, 0));

function pluralize(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function currentZoom() {
  return map?.getZoom() ?? COUNTRY_ZOOM;
}

// Circles are sized in meters, so their pixel radius is converted at the current
// zoom and their own latitude to keep the same size on screen everywhere.
function drawBubbles(frame: PulseFrame) {
  const zoom = currentZoom();
  bubbles.forEach(({ circle, radiusPx, lat }) => {
    circle.setOptions({ radius: radiusPx * metersPerPixel(zoom, lat) * frame.scale, fillOpacity: frame.opacity });
  });
}

function currentFrame() {
  return prefersReducedMotion() ? RESTING_FRAME : pulseFrame(Date.now());
}

function startPulse() {
  if (pulseTimer || prefersReducedMotion()) return;

  pulseTimer = setInterval(() => drawBubbles(pulseFrame(Date.now())), PULSE_FRAME_MS);
}

function stopPulse() {
  if (pulseTimer) clearInterval(pulseTimer);
  pulseTimer = null;
}

function textLine(text: string, style: Partial<CSSStyleDeclaration> = {}) {
  const line = document.createElement('div');
  line.textContent = text;
  Object.assign(line.style, style);

  return line;
}

// Built from DOM nodes (textContent), never interpolated HTML: names are user data.
function buildInfoContent(location: ShiftLocation): HTMLElement {
  const content = document.createElement('div');
  content.style.lineHeight = '1.4';
  location.shifts.forEach((shift, index) => {
    const entry = document.createElement('div');
    if (index > 0) entry.style.marginTop = '8px';
    const caregiver = [shift.caregiverFullName, shift.caregiverHumanCareer].filter(Boolean).join(' · ');
    entry.append(
      textLine(shift.patientFullName, { fontWeight: '600' }),
      textLine(caregiver),
      textLine(`${shift.humanTime} · ${shift.status.name}`, { color: '#666' }),
    );
    content.appendChild(entry);
  });

  return content;
}

function openInfo(location: ShiftLocation) {
  if (!map || !infoWindow) return;

  infoWindow.setContent(buildInfoContent(location));
  infoWindow.setPosition(location.position);
  infoWindow.open(map);
}

function clearBubbles() {
  bubbles.forEach(({ circle }) => circle.setMap(null));
  bubbles = [];
}

// Frames the shifts once: later refreshes keep whatever zoom the user chose.
function fitToShifts() {
  if (!map || hasFitBounds || locations.value.length === 0) return;

  const bounds = new google.maps.LatLngBounds();
  locations.value.forEach(location => location.shifts.forEach((shift) => {
    bounds.extend({ lat: shift.latitude as number, lng: shift.longitude as number });
  }));
  map.fitBounds(bounds, FIT_BOUNDS_PADDING);
  const zoom = map.getZoom();
  if (zoom && zoom > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
  hasFitBounds = true;
}

function renderBubbles() {
  if (!map) return;

  clearBubbles();
  infoWindow?.close();
  const maxCount = Math.max(0, ...locations.value.map(location => location.shifts.length));
  bubbles = locations.value.map((location) => {
    const circle = new google.maps.Circle({
      map,
      center: location.position,
      fillColor: BUBBLE_COLOR,
      strokeColor: BUBBLE_COLOR,
      strokeOpacity: BUBBLE_STROKE_OPACITY,
      strokeWeight: BUBBLE_STROKE_WEIGHT,
      clickable: true,
      zIndex: Z_INDEX.bubble,
    });
    circle.addListener('click', () => openInfo(location));

    return { circle, radiusPx: bubbleRadius(location.shifts.length, maxCount), lat: location.position.lat };
  });
  drawBubbles(currentFrame());
  fitToShifts();
  if (bubbles.length > 0) startPulse();
  else stopPulse();
}

// The minute refresh returns new objects even when nothing moved: redraw only
// when a bubble's position, shifts or status changed, so an open popup survives.
const bubblesSignature = computed(() => JSON.stringify(locations.value.map(location => [
  location.position, location.shifts.map(shift => [shift.id, shift.status.name]),
])));

watch(bubblesSignature, renderBubbles);

function companyVeilLoader() {
  const code = currentUser?.value?.companyCountryCode;

  return code ? countryVeils[`./country-veils/${code.toLowerCase()}.json`] : undefined;
}

// Dims the land of the countries around the company's, so only its own country
// reads. A country without a veil file keeps the plain map.
async function drawCountryVeil() {
  const loadVeil = companyVeilLoader();
  if (!loadVeil) return;

  const rings = await loadVeil();
  if (!map) return; // unmounted while the veil loaded
  veil = rings.map(ring => new google.maps.Polygon({
    map,
    paths: veilPath(ring),
    fillColor: VEIL_COLOR,
    fillOpacity: VEIL_OPACITY,
    strokeWeight: 0,
    clickable: false,
    zIndex: Z_INDEX.veil,
  }));
}

function onZoomChanged() {
  groupingZoom.value = Math.floor(currentZoom());
  drawBubbles(currentFrame());
}

onMounted(async () => {
  if (!mapContainer.value) return;

  try {
    const { Map, InfoWindow } = await importLibrary('maps');
    map = new Map(mapContainer.value, {
      center: companyMapCenter.value,
      zoom: COUNTRY_ZOOM,
      mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative',
      colorScheme: 'DARK',
    });
    infoWindow = new InfoWindow();
    map.addListener('zoom_changed', onZoomChanged);
    renderBubbles();
    // The veil is cosmetic: if its file fails to load, the map works without it.
    drawCountryVeil().catch(() => undefined);
  } catch {
    mapError.value = true;
  }
});

onUnmounted(() => {
  stopPulse();
  infoWindow?.close();
  clearBubbles();
  veil.forEach(polygon => polygon.setMap(null));
  veil = [];
  map = null;
});
</script>
<template>
  <v-card elevation="2">
    <div class="d-flex flex-wrap align-center ga-2 px-4 py-3">
      <span class="text-subtitle-2 font-weight-bold">Turnos activos en vivo</span>
      <v-spacer />
      <span
        v-if="!loading"
        class="text-body-2 text-medium-emphasis"
      >
        {{ pluralize(shifts.length, 'turno activo', 'turnos activos') }}
        <template v-if="mappedCount < shifts.length">
          · {{ mappedCount }} en mapa
        </template>
      </span>
    </div>
    <v-divider />
    <div class="today-map">
      <div
        ref="mapContainer"
        class="today-map__canvas"
        role="region"
        aria-label="Mapa de turnos activos"
      />
      <div
        v-if="mapError"
        class="today-map__overlay"
        role="alert"
      >
        No se pudo cargar el mapa.
      </div>
      <div
        v-else-if="loading"
        class="today-map__overlay"
      >
        Cargando mapa...
      </div>
      <div
        v-else-if="shifts.length === 0"
        class="today-map__overlay"
      >
        No hay turnos activos en este momento.
      </div>
    </div>
  </v-card>
</template>
<style scoped>
.today-map {
  position: relative;
  height: 450px;
}

.today-map__canvas {
  width: 100%;
  height: 100%;
}

.today-map__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  /* Over the dark map: a dim veil with light text, not a white flash. */
  background: rgba(18, 18, 18, .75);
  color: #fff;
  pointer-events: none;
}

@media (max-width: 600px) {
  .today-map {
    height: 360px;
  }
}
</style>
